import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { orderBy } from 'lodash';
import {
  addItemsToMapList,
  removeItemsFromMapList,
  updateItemInMapList,
  updateItemsInMapList,
} from '../../../helpers/reduxHelper';
import { selectSelectedLabelId, selectSelectedSceneId } from './configurator.slicer';
import { EMPTY_ARRAY } from '../../../constants/productConfiguratorConstants';
import { isV2 } from '../../../components/Configurator/utils';

function defaultLabels(state, sceneId, loading = false, loaded = true) {
  if (state.data[sceneId]) return;
  state.data[sceneId] = {
    loading,
    loaded,
    error: false,
    labels: [],
  };
}

const getInitialState = () => ({
  data: {},
});

const configuratorSceneLabels = createSlice({
  name: 'configuratorSceneLabels',
  initialState: getInitialState(),
  reducers: {
    resetState: () => {
      return getInitialState();
    },
    fetchSceneLabelsStart: (state, { payload }) => {
      const { sceneId } = payload;
      defaultLabels(state, sceneId, true, false);
      state.data[sceneId].loading = true;
      state.data[sceneId].loaded = false;
      state.data[sceneId].error = false;
      state.data[sceneId].labels = [];
    },
    fetchSceneLabelsSuccess: (state, { payload }) => {
      const { sceneId, labels } = payload;
      defaultLabels(state, sceneId);
      state.data[sceneId].loading = false;
      state.data[sceneId].loaded = true;
      state.data[sceneId].error = false;
      state.data[sceneId].labels = labels;
    },
    fetchSceneLabelsFailed: (state, { payload }) => {
      const { sceneId } = payload;
      defaultLabels(state, sceneId);
      state.data[sceneId].loading = false;
      state.data[sceneId].loaded = true;
      state.data[sceneId].error = true;
      state.data[sceneId].labels = [];
    },
    addLabelsToScene: (state, action) => {
      defaultLabels(state, action.payload.sceneId);
      addItemsToMapList('labels', 'sceneId', 'labels', 'data')(state, action);
    },
    removeLabelsFromScene: removeItemsFromMapList('labels', 'sceneId', 'data'),
    updateLabel: updateItemInMapList('labels', 'sceneId', 'label', 'data'),
    changeLabelsOrder: updateItemsInMapList('labels', 'sceneId', 'labels', 'data'),
    updateLabels: updateItemsInMapList('labels', 'sceneId', 'labels', 'data'),
  },
});

export const configuratorSceneLabelsActions = configuratorSceneLabels.actions;
export const configuratorSceneLabelsReducer = configuratorSceneLabels.reducer;

export const selectSceneLabels =
  (sceneId) =>
  (state) => {
    const labels = state.configuratorSceneLabels.data[sceneId]?.labels || EMPTY_ARRAY;
    return labels;
  };

export const selectCurrentSceneLabels = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneLabels(sceneId)(state);
};

export const selectSceneLabelsLoaded = (sceneId) => (state) => {
  return state.configuratorSceneLabels.data[sceneId]?.loaded;
};

export const selectSceneLabelsLoading = (sceneId) => (state) => {
  return state.configuratorSceneLabels.data[sceneId]?.loading;
};

export const selectSceneLabelsFailed = (sceneId) => (state) => {
  return Boolean(state.configuratorSceneLabels.data[sceneId]?.error);
};

export const selectCurrentSceneSortedLabels = createSelector(
  (state) => selectCurrentSceneLabels(state),
  (labels) => {
    return orderBy(labels, isV2() ? 'data.seq_no' : 'seq_no', 'asc');
  }
);

export const selectSelectedLabel = (state) => {
  const labelId = selectSelectedLabelId(state);
  return selectCurrentSceneLabels(state).find((l) => l.id === labelId);
};

export const selectAllLinkedObjects = (state) => {
  const sceneId = selectSelectedSceneId(state);
  const labels = selectSceneLabels(sceneId)(state);
  return labels.reduce((acc, l) => {
    if (l.data.linkedObjects) {
      acc.push(...l.data.linkedObjects);
    }
    return acc;
  }, []);
};

export const selectLabelById = (id, sceneId) => (state) => {
  const label = selectSceneLabels(sceneId)(state).find((l) => l.id === id);
  return label;
};
